/**
 * @fileoverview MainAppStartFlow - Core Flow Orchestration Module
 * 
 * This module serves as the central flow orchestrator for the desktop application.
 * It manages and coordinates different workflow scenarios including:
 * - Home Page Flow
 * - My Account Flow
 * - Train Offer Flow
 * 
 * The orchestrator ensures proper flow execution, handles flow transitions,
 * and manages the lifecycle of active flows.
 * 
 */
import { _eventBus } from "../../core/events/EventBus";
import { WorkFlowOrchestrator } from "../../core/flows/WorkFlowOrchestrator";
import { getPersistentFlowManager } from "../../core/flows/PersistentFlowManager";
import { afterSalesBookingStartupFlow } from "../AfterSalesBookingFlow/AfterSalesBookingStartupFlow";
import { BookingDetailStartupFlow } from "../BookingDetails/BookingDetailStartup";
import { cancelBookingMainStartupFlow } from "../CancelBooking/CancelBookingStartup";
import { changeDateMainStartupFlow } from "../ChangeDate/ChangeDateStartup";
import { checkoutStartupFlow } from "../CheckoutPage/CheckoutStartup";
import { DestinationDetailStartupFlow } from "../DestinationDetailsFlow/DestinationDetailStartup";
import { DestinationsStartupFlow } from "../Destinations/DestinationsStartup";
import { FmlmViewStartupFlow } from "../FmlmDetail/FmlmViewStartup";
import { bannerStartupFlow } from "../GetAppBannerFlow/BannerStartup";
import { homePageMainStartupFlow } from "../HomePage/HomePageStartup";
import { myAccountMainStartupFlow } from "../MyAccountPage/MyAccountStartup";
import { needHelpStartupFlow } from "../NeedHelpFlow/NeedHelpStartup";
import { OffersStartupFlow } from "../Offers/OffersStartup";
import { ServiceRequestClaimStartupFlow } from "../ServiceRequestClaim/ServiceRequestClaimStartup";
import { ServiceRequestClaimDetailsStartupFlow } from "../ServiceRequestClaimDetails/ServiceRequestClaimDetailsStartup";
import { TermsOfUseStartupFlow } from "../TermsOfUse/TermsOfUseStartup";
import { cookiesStartupFlow } from "../Cookies/CookiesStartup";
import { topBarStartupFlow } from "../TopBar/TopBarStartup";
import { customerContactStartupFlow } from "../NeedHelpCustomContact/CustomerContactStartup";
import { requestCorporateAccountFlow } from "../CorporateAccount/RequestCorporateAccount/MainBookingFlow/RequestCorporateAccountFlow";
import { NetworkMapStationStartupFlow } from "../NetworkMapStation/NetworkMapStationStartup";
import { NetworkDetailStartupFlow } from "../NetworkDetail/NetworkMapStationStartup";
import { PrivacyPolicyStartupFlow } from "../PrivacyPolicy/PrivacyPolicyStartup";
import { NewsDetailStartupFlow } from "../NewsArticleDetail/NewsDetailStartup";
import { mainBookingStartupFlow } from "../MainBookingFlow/MainBookingStartupFlow";
import { CMSPlaceStartupFlow } from "../DestinationFamousPlace/FamousPlaceStartup";
import { upgradeBookingStartupFlow } from "../UpgradeBooking/UpgradeBookingStartup";
import { JourneyPlannerMenuStartupFlow } from "../JourneyPlannerMenuFlow/JourneyPlannerMenuStartup";
import { EnhancedTripDetailStartupFlow } from "../EnhancedTripDetailsFlow/EnhancedTripDetailStartup";
import { UsefulInfoDetailStartupFlow } from "../UsefulInfoDetailsFlow/UsefulInfoDetailStartup";
import { addShuttleTransferStartupFlow } from "../AddShuttleTransfer/AddShuttleTransferStartup";
import { OfferDetailStartupFlow } from "../OffersDetailFlow/OffersDetailStartup";
import { personalizeExperienceMainFlow } from "../MyAccountPage/PersonalizeExperience/PersonalizeExperienceStartup";
import { GroupBookingStartupFlow } from "../GroupBooking/GroupBookingStartup";
import { PlanMultiBookStartupFlow } from "../PlanMultiBook/PlanMultiBookStartup";
import { MultiBookingStartupFlow } from "../MultiBooking/MultiBookingStartup";
import { myCompanyAccountStartupFlow } from "../MyCompanyAccountPage/MyCompanyAccountStartup";


/**
 * Main orchestrator instance that manages all application flows
 */
const orchestrator = new WorkFlowOrchestrator<any>(_eventBus, !true);

/**
 * Persistent flow manager for all flows
 */
const persistentFlowManager = getPersistentFlowManager(orchestrator);

/**
 * Register all available flows with the orchestrator
 * Each flow represents a different user journey or application state
 */
orchestrator.addFlow(homePageMainStartupFlow);
orchestrator.addFlow(mainBookingStartupFlow);
orchestrator.addFlow(topBarStartupFlow);
orchestrator.addFlow(cookiesStartupFlow);
orchestrator.addFlow(myAccountMainStartupFlow);
orchestrator.addFlow(checkoutStartupFlow);
orchestrator.addFlow(afterSalesBookingStartupFlow);
orchestrator.addFlow(BookingDetailStartupFlow);
orchestrator.addFlow(cancelBookingMainStartupFlow);
orchestrator.addFlow(changeDateMainStartupFlow);
orchestrator.addFlow(FmlmViewStartupFlow)
orchestrator.addFlow(bannerStartupFlow);
orchestrator.addFlow(customerContactStartupFlow);
orchestrator.addFlow(needHelpStartupFlow);
orchestrator.addFlow(DestinationsStartupFlow);
orchestrator.addFlow(DestinationDetailStartupFlow);
orchestrator.addFlow(EnhancedTripDetailStartupFlow);
orchestrator.addFlow(UsefulInfoDetailStartupFlow);

orchestrator.addFlow(ServiceRequestClaimStartupFlow);
orchestrator.addFlow(ServiceRequestClaimDetailsStartupFlow);
orchestrator.addFlow(CMSPlaceStartupFlow);
orchestrator.addFlow(OffersStartupFlow);
orchestrator.addFlow(OfferDetailStartupFlow);
orchestrator.addFlow(NewsDetailStartupFlow);

orchestrator.addFlow(TermsOfUseStartupFlow);
orchestrator.addFlow(requestCorporateAccountFlow);
orchestrator.addFlow(PrivacyPolicyStartupFlow);

orchestrator.addFlow(NetworkMapStationStartupFlow);
orchestrator.addFlow(NetworkDetailStartupFlow);
orchestrator.addFlow(JourneyPlannerMenuStartupFlow);
orchestrator.addFlow(requestCorporateAccountFlow);

orchestrator.addFlow(upgradeBookingStartupFlow);

orchestrator.addFlow(addShuttleTransferStartupFlow);

orchestrator.addFlow(personalizeExperienceMainFlow);

orchestrator.addFlow(GroupBookingStartupFlow);
orchestrator.addFlow(PlanMultiBookStartupFlow);

orchestrator.addFlow(MultiBookingStartupFlow);

orchestrator.addFlow(myCompanyAccountStartupFlow);

/**
 * Maintains a list of active flow runners
 * Used to track and manage concurrent flows
 */
let orchestratorFlowRunnerList: any = []

/**
 * Executes a specific flow scenario
 * @param flowName - The name of the flow to execute
 * Flow Execution Process:
 * 1. Checks for existing flow instance
 * 2. Cancels existing flow if found
 * 3. Cleans up flow runner list
 * 4. Creates and registers new flow instance
 */
const executeScenario = async (flowName: string) => {
    orchestrator.verbose = true
    // persistence flow
    if (flowName == "CheckoutFlow") {
        try {
            // Cancel all existing Checkout flow runners before starting a new one.
            const existingFlows = orchestratorFlowRunnerList.filter((d: any) => d.flowName === flowName);
            if (existingFlows.length > 0) {
                console.log(`${flowName} flow is already running, cancelling existing runners first`);
                existingFlows.forEach((flow: any) => orchestrator.cancel(flow.flowRunnerId));
                persistentFlowManager.cancelPersistedFlow(flowName);
                orchestratorFlowRunnerList = orchestratorFlowRunnerList.filter((d: any) => d.flowName !== flowName);
            }

            const runnerId = await persistentFlowManager.executeWithPersistence(flowName);

            // Ensure we got a valid runner ID before proceeding
            if (!runnerId) {
                return;
            }

            // Update the orchestrator flow runner list
            orchestratorFlowRunnerList.push({
                flowName: flowName,
                flowRunnerId: runnerId
            });

            return;
        } catch (error) {
            // Fallback to regular flow execution if persistence fails
            try {
                const fallbackRunnerId = orchestrator.execute(flowName);
                orchestratorFlowRunnerList.push({
                    flowName: flowName,
                    flowRunnerId: fallbackRunnerId
                });
            } catch (fallbackError) {
                console.error('MainAppStartFlow Fallback execution also failed:', fallbackError);
            }
        }
    }

    // Regular flow execution for other flows
    const existingFlow = orchestratorFlowRunnerList.find((d: any) => d.flowName === flowName)

    if (existingFlow) {
        console.log(`${flowName} flow is already running so first orchestrator close ${flowName} flow and start again`);
        orchestrator.cancel(existingFlow.flowRunnerId)
        orchestratorFlowRunnerList = orchestratorFlowRunnerList.filter((d: any) => d.flowRunnerId != existingFlow.flowRunnerId)
    } else {
        //No existing flow instance found
    }

    const runnerId = orchestrator.execute(flowName);
    console.log(`Orchestrator executed flow: ${flowName}`);

    orchestratorFlowRunnerList.push({ flowName: flowName, flowRunnerId: runnerId })
};


/**
 * Main entry point for flow execution
 * Listens for flow change events and manages flow
 * @param flowName - Initial flow to execute
 * @method mainAppStartupFlowExecute - Method manage which flow need to run at,
 *  initial level and if page refresh by user.
 */
export const mainAppStartupFlowExecute = async (flowName: string) => {
    // Clean up old flows on startup
    persistentFlowManager.cleanup();

    if (flowName) {
        executeScenario(flowName)
    }
}

/**
 * @event FLOW_CHANGE - Manage which flow need's to run and this event will trigger,
 *  outside the orchestrator(eg. From any flow Steps) 
 */
_eventBus.on<string, void>({
    eventName: "FLOW_CHANGE",
    handler: async ({ data }) => {
        if (data)
            executeScenario(data);
    }
});


/**
 * Added this method to close or Tear down all other flow which is already running.
 */
const executeScenarioForCloseFlow = async (flowName: string) => {
    orchestrator.verbose = true
    const existingFlow = orchestratorFlowRunnerList.find((d: any) => d.flowName === flowName)

    if (existingFlow) {
        orchestrator.cancel(existingFlow.flowRunnerId)
        console.log(`Orchestrator closed flow: ${flowName}`);
        orchestratorFlowRunnerList = orchestratorFlowRunnerList.filter((d: any) => d.flowRunnerId != existingFlow.flowRunnerId)
    }
};

/**
 * @event FLOW_CLOSE - Manage which flow need's to close and this event will trigger,
 *  outside the orchestrator(eg. From any flow Steps) 
 */
_eventBus.on<string, void>({
    eventName: "FLOW_CLOSE",
    handler: async ({ data }) => {
        if (data) {
            if (data == "CheckoutFlow") {
                persistentFlowManager.cancelPersistedFlow(data);
                const existingFlows = orchestratorFlowRunnerList.filter((d: any) => d.flowName === data);
                existingFlows.forEach((flow: any) => orchestrator.cancel(flow.flowRunnerId));
                orchestratorFlowRunnerList = orchestratorFlowRunnerList.filter((d: any) => d.flowName !== data);
            } else {
                executeScenarioForCloseFlow(data);
            }
        }
    }
});